import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  Snackbar,
  Chip,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Calculate as CalculateIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  CloudUpload as SubmitIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Types
interface SalesTaxReturn {
  period_start: string;
  period_end: string;
  vat_due_sales: number;
  vat_due_acquisitions: number;
  total_vat_due: number;
  vat_reclaimed: number;
  net_vat_due: number;
  total_value_sales: number;
  total_value_purchases: number;
  total_value_goods_supplied: number;
  total_acquisitions: number;
  vat_due_sales_formatted: string;
  vat_due_acquisitions_formatted: string;
  total_vat_due_formatted: string;
  vat_reclaimed_formatted: string;
  net_vat_due_formatted: string;
  total_value_sales_formatted: string;
  total_value_purchases_formatted: string;
  sales_lines: SalesTaxReturnLine[];
  purchase_lines: SalesTaxReturnLine[];
}

interface SalesTaxReturnLine {
  description: string;
  net_amount: number;
  vat_rate: string;
  vat_amount: number;
  total_amount: number;
  net_amount_formatted: string;
  vat_amount_formatted: string;
  total_amount_formatted: string;
}

interface VATSummary {
  period_start: string;
  period_end: string;
  sales_vat: number;
  purchase_vat: number;
  net_vat_due: number;
  return_due_date: string;
  status: string;
}

const SalesTaxReturns: React.FC = () => {
  const [salesTaxReturn, setVatReturn] = useState<SalesTaxReturn | null>(null);
  const [taxSummary, setVatSummary] = useState<VATSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [periodStart, setPeriodStart] = useState<Date>(new Date(new Date().getFullYear(), new Date().getMonth() - 3, 1));
  const [periodEnd, setPeriodEnd] = useState<Date>(new Date(new Date().getFullYear(), new Date().getMonth(), 0));
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    fetchVATData();
  }, [periodStart, periodEnd]);

  const fetchVATData = async () => {
    try {
      setLoading(true);

      // Fetch Sales Tax return data
      const startDate = periodStart.toISOString().split('T')[0];
      const endDate = periodEnd.toISOString().split('T')[0];

      const [returnResponse, summaryResponse] = await Promise.all([
        fetch(`/api/v1/reports/vat-return?start_date=${startDate}&end_date=${endDate}`),
        fetch('/api/v1/dashboard/vat-summary')
      ]);

      if (returnResponse.ok) {
        const returnData = await returnResponse.json();
        setVatReturn(returnData);
      }

      if (summaryResponse.ok) {
        const summaryData = await summaryResponse.json();
        setVatSummary(summaryData);
      }
    } catch (error) {
      console.error('Error fetching Sales Tax data:', error);
      setSnackbar({ open: true, message: 'Failed to load Sales Tax data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReturn = async () => {
    try {
      setSubmitting(true);

      // In a real implementation, this would submit to IRS via E-Filing API
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate API call

      setSnackbar({ open: true, message: 'Sales Tax return submitted successfully to IRS!', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to submit Sales Tax return', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayNow = async () => {
    try {
      if (!salesTaxReturn) return;

      setSubmitting(true);
      const response = await fetch('/api/cfo/irs/pay-tax', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: salesTaxReturn.net_vat_due,
          return_url: window.location.href + "?status=success",
          back_url: window.location.href + "?status=cancelled"
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.nextUrl) {
          window.location.href = data.nextUrl;
        }
      } else {
        setSnackbar({ open: true, message: 'Failed to initiate IRS payment', severity: 'error' });
      }
    } catch (error) {
      console.error("Payment Error:", error);
      setSnackbar({ open: true, message: 'Payment error', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'draft': return 'default';
      case 'submitted': return 'success';
      case 'overdue': return 'error';
      default: return 'info';
    }
  };

  const formatVATBox = (boxNumber: number, label: string, value: string, description: string) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" color="primary">
              Box {boxNumber}: {label}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {description}
            </Typography>
          </Box>
          <Typography variant="h5" fontWeight="bold">
            {value}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Sales Tax Returns</Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Sales Tax Returns - IRS E-Filing
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
            >
              Download PDF
            </Button>
            <Button
              variant="contained"
              startIcon={<SubmitIcon />}
              onClick={handleSubmitReturn}
              disabled={submitting}
              color="success"
            >
              {submitting ? 'Submitting...' : 'Submit to IRS'}
            </Button>
          </Box>
        </Box>

        {/* Period Selection */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Tax Period
            </Typography>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Period Start"
                  value={periodStart}
                  onChange={(date) => setPeriodStart(date || new Date())}
                  slotProps={{
                    textField: { fullWidth: true }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Period End"
                  value={periodEnd}
                  onChange={(date) => setPeriodEnd(date || new Date())}
                  slotProps={{
                    textField: { fullWidth: true }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="outlined"
                  startIcon={<CalculateIcon />}
                  onClick={fetchVATData}
                  fullWidth
                >
                  Recalculate
                </Button>
              </Grid>
              <Grid item xs={12} md={3}>
                {taxSummary && (
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Due Date: {new Date(taxSummary.return_due_date).toLocaleDateString('en-US')}
                    </Typography>
                    <Chip
                      label={taxSummary.status.toUpperCase()}
                      color={getStatusColor(taxSummary.status) as any}
                      size="small"
                    />
                  </Box>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* E-Filing Compliance Status */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>IRS E-Filing Compliance:</strong> This system is designed to meet IRS's e-filing requirements.
            All calculations are performed automatically from your transaction data.
          </Typography>
        </Alert>

        {salesTaxReturn && (
          <Grid container spacing={3}>
            {/* Sales Tax Return Boxes */}
            <Grid item xs={12} md={8}>
              <Typography variant="h5" gutterBottom>
                Sales Tax Return - {new Date(salesTaxReturn.period_start).toLocaleDateString('en-US')} to {new Date(salesTaxReturn.period_end).toLocaleDateString('en-US')}
              </Typography>

              {/* Box 1-5: Sales Tax Calculations */}
              {formatVATBox(1, "Sales tax due on sales", salesTaxReturn.vat_due_sales_formatted, "Sales tax charged on your sales and other outputs")}
              {formatVATBox(2, "Sales tax due on acquisitions", salesTaxReturn.vat_due_acquisitions_formatted, "Sales tax due on acquisitions from other states")}
              {formatVATBox(3, "Total sales tax due", salesTaxReturn.total_vat_due_formatted, "Total of Box 1 and Box 2")}
              {formatVATBox(4, "Sales tax reclaimed", salesTaxReturn.vat_reclaimed_formatted, "Sales tax reclaimed on purchases and other inputs")}

              <Card sx={{ mb: 2, bgcolor: salesTaxReturn.net_vat_due >= 0 ? 'error.light' : 'success.light' }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h6" color="primary">
                        Box 5: Net sales tax {salesTaxReturn.net_vat_due >= 0 ? 'due' : 'refund'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {salesTaxReturn.net_vat_due >= 0 ? 'Amount to pay to IRS' : 'Amount IRS will refund to you'}
                      </Typography>
                    </Box>
                    <Typography variant="h4" fontWeight="bold" color={salesTaxReturn.net_vat_due >= 0 ? 'error.main' : 'success.main'}>
                      {salesTaxReturn.net_vat_due_formatted}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              {/* Box 6-9: Turnover Figures */}
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>Turnover and Other Outputs</Typography>

              {formatVATBox(6, "Total value of sales", salesTaxReturn.total_value_sales_formatted, "Total value of sales and all other outputs excluding sales tax")}
              {formatVATBox(7, "Total value of purchases", salesTaxReturn.total_value_purchases_formatted, "Total value of purchases and all other inputs excluding sales tax")}
              {formatVATBox(8, "Total value of goods supplied", `$${(salesTaxReturn.total_value_goods_supplied || 0).toFixed(2)}`, "Total value of all supplies of goods to other states")}
              {formatVATBox(9, "Total acquisitions", salesTaxReturn.vat_due_acquisitions_formatted, "Total value of acquisitions of goods from other states")}
            </Grid>

            {/* Summary and Actions */}
            <Grid item xs={12} md={4}>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <CheckIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Return Summary
                  </Typography>

                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Period:</Typography>
                    <Typography variant="body2">
                      {new Date(salesTaxReturn.period_start).toLocaleDateString('en-US')} - {new Date(salesTaxReturn.period_end).toLocaleDateString('en-US')}
                    </Typography>
                  </Box>

                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Sales Transactions:</Typography>
                    <Typography variant="body2">{salesTaxReturn.sales_lines.length}</Typography>
                  </Box>

                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Purchase Transactions:</Typography>
                    <Typography variant="body2">{salesTaxReturn.purchase_lines.length}</Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box display="flex" justifyContent="space-between" mb={2}>
                    <Typography variant="h6">Net Sales Tax:</Typography>
                    <Typography variant="h6" color={salesTaxReturn.net_vat_due >= 0 ? 'error.main' : 'success.main'}>
                      {salesTaxReturn.net_vat_due_formatted}
                    </Typography>
                  </Box>

                  {taxSummary && (
                    <Alert
                      severity={salesTaxReturn.net_vat_due >= 0 ? 'warning' : 'success'}
                      action={
                        salesTaxReturn.net_vat_due >= 0 ? (
                          <Button
                            color="inherit"
                            size="small"
                            onClick={handlePayNow}
                            disabled={submitting}
                            sx={{ fontWeight: 'bold' }}
                          >
                            {submitting ? 'Connecting...' : 'PAY NOW'}
                          </Button>
                        ) : null
                      }
                    >
                      <Typography variant="body2">
                        {salesTaxReturn.net_vat_due >= 0
                          ? `Payment due by ${new Date(taxSummary.return_due_date).toLocaleDateString('en-US')}`
                          : 'Refund will be processed by IRS'
                        }
                      </Typography>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* E-Filing Submission Status */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    E-Filing Submission
                  </Typography>

                  <Typography variant="body2" color="textSecondary" paragraph>
                    This return will be submitted directly to IRS via the IRS E-Filing API.
                  </Typography>

                  <Box display="flex" flexDirection="column" gap={1}>
                    <Button
                      variant="outlined"
                      startIcon={<ViewIcon />}
                      fullWidth
                    >
                      Preview Return
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      fullWidth
                    >
                      Download Backup
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default SalesTaxReturns;
